import { useState, useEffect } from "react";
import { Phone, CheckCircle2, AlertCircle, Save, RefreshCw, PlugZap } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  getTelephonySettings, saveTelephonySettings, testTelephony,
  type TelephonyStatus, type TelephonyTestResult,
} from "../../../../lib/calls";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { CopyPill, Field, FieldGroup, Note, Pill, SaveRow, SecretPill, SelectPill, ToggleRow } from "../panel/fields";
import { HoldMusicField } from "./HoldMusicField";
import { RingtoneField } from "./RingtoneField";
import { LookupFields } from "./LookupFields";
import { telephonyChecklist } from "./telephonyChecklist";

/** Twilio Voice SDK edge locations relevant from Estonia (empty = global default). */
const EDGE_OPTIONS = ["", "dublin", "frankfurt", "ashburn", "umatilla"] as const;

/**
 * Twilio home regions (empty = us1, the default). This is where the credentials
 * live, which is a different question from the edge above: keys, auth tokens and
 * TwiML apps created in one region are invisible from the others.
 */
const REGION_OPTIONS = ["", "ie1", "au1"] as const;

/** Settings -> Integrations: Twilio credentials for browser calling + recording. */
export function TelephonyIntegrationCard() {
  const t = useT();
  const [s, setS] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error" | "secretRequired"
  >("idle");
  const [testState, setTestState] = useState<"idle" | "testing">("idle");
  const [testResult, setTestResult] = useState<TelephonyTestResult | null>(null);

  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [apiKeySid, setApiKeySid] = useState("");
  const [apiKeySecret, setApiKeySecret] = useState("");
  const [twimlAppSid, setTwimlAppSid] = useState("");
  const [pushCredentialSid, setPushCredentialSid] = useState("");
  const [callerId, setCallerId] = useState("");
  const [record, setRecord] = useState(true);
  // A string like every other numeric field on this page: a controlled number
  // input cannot hold the empty box a half-typed value passes through.
  const [outboundRingSeconds, setOutboundRingSeconds] = useState("30");
  const [region, setRegion] = useState("");
  const [edge, setEdge] = useState("");
  const [conference, setConference] = useState(false);
  const [holdUrl, setHoldUrl] = useState("");
  const [ringtoneUrl, setRingtoneUrl] = useState("");
  const [ringbackUrl, setRingbackUrl] = useState("");
  const [legDualSplit, setLegDualSplit] = useState(false);
  const [publicBaseUrl, setPublicBaseUrl] = useState("");

  function hydrate(data: TelephonyStatus) {
    setS(data);
    setAccountSid(data.twilio.accountSid);
    setAuthToken("");
    setApiKeySid(data.twilio.apiKeySid);
    setApiKeySecret("");
    setTwimlAppSid(data.twilio.twimlAppSid);
    setPushCredentialSid(data.twilio.pushCredentialSid ?? "");
    setCallerId(data.twilio.callerId);
    setRecord(data.twilio.record);
    setOutboundRingSeconds(String(data.twilio.outboundRingSeconds));
    setRegion(data.twilio.region);
    setEdge(data.twilio.edge);
    setConference(data.twilio.conference);
    setHoldUrl(data.twilio.holdUrl);
    setRingtoneUrl(data.twilio.ringtoneUrl ?? "");
    setRingbackUrl(data.twilio.ringbackUrl ?? "");
    setLegDualSplit(data.twilio.legDualSplit);
    setPublicBaseUrl(data.publicBaseUrl);
  }

  useEffect(() => {
    getTelephonySettings().then(hydrate).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      const updated = await saveTelephonySettings({
        // purgeOrphanRecordings is deliberately absent: it belongs to
        // RecordingStorageCard, and the PUT is a partial merge that keeps stored
        // fields it is not given. Sending it here would write back a stale value.
        twilio: {
          accountSid, authToken, apiKeySid, apiKeySecret, twimlAppSid, pushCredentialSid, callerId, record,
          region, edge, conference, holdUrl, ringtoneUrl, ringbackUrl, legDualSplit,
          // An empty or unparseable box means "leave it alone" rather than zero:
          // `Number("")` is 0, and a zero here would be clamped up to the floor,
          // which is a ring time nobody chose.
          ...(Number.isFinite(Number(outboundRingSeconds)) && outboundRingSeconds.trim()
            ? { outboundRingSeconds: Number(outboundRingSeconds) }
            : {}),
        },
        publicBaseUrl,
      });
      hydrate(updated);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      // Left on screen rather than timed out: the operator has to go back to
      // the Twilio console for the secret, and a warning that vanished while
      // they were in another tab would be no warning at all.
      const secretRequired = err instanceof ApiError && err.message === "api_key_secret_required";
      setSaveState(secretRequired ? "secretRequired" : "error");
    }
  }

  async function handleTest() {
    setTestState("testing");
    setTestResult(null);
    try {
      setTestResult(await testTelephony());
    } catch {
      // The whole result, so the checklist below can stay one render path. The
      // diagnostics are the shape a server that could reach nothing returns.
      setTestResult({
        twilio: { ok: false, error: "network" },
        gemini: { ok: false, error: "network" },
        elevenlabs: { ok: false, error: "network" },
        inbound: {
          ok: false,
          appUrlMatches: false,
          appVoiceUrl: "",
          appMethodMatches: false,
          numbers: [],
          truncated: false,
          error: "twilio_upstream",
        },
        diagnostics: {
          keyPair: { state: "unknown", reason: "twilio_unreachable" },
          region: { state: "unknown", reason: "twilio_unreachable" },
          callerId: { state: "unknown", reason: "twilio_unreachable" },
          account: { state: "unknown", reason: "twilio_unreachable" },
        },
      });
    } finally {
      setTestState("idle");
    }
  }

  const checklist = telephonyChecklist(testResult?.diagnostics);

  if (loading) return <PanelLoader />;

  const configured = !!s?.twilio.configured;

  return (
    <IntegrationCard
      icon={Phone}
      title={t("settings.telephony.title")}
      desc={t("settings.telephony.desc")}
      status={
        <ConnStatus
          ok={configured}
          okLabel={t("settings.telephony.statusConfigured")}
          offLabel={t("settings.telephony.statusNotConfigured")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-6">
        <FieldGroup title={t("settings.telephony.account")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t("settings.telephony.accountSid")} *`}>
              <Pill value={accountSid} onChange={setAccountSid} placeholder="AC..." autoComplete="off" mono />
            </Field>

            <Field
              label={`${t("settings.telephony.authToken")} *`}
              hint={s?.twilio.authTokenSet && !authToken ? t("settings.telephony.secretKept") : undefined}
            >
              <SecretPill
                value={authToken}
                onChange={setAuthToken}
                placeholder={s?.twilio.authTokenSet ? "••••••••" : ""}
              />
            </Field>

            <Field label={`${t("settings.telephony.apiKeySid")} *`}>
              <Pill value={apiKeySid} onChange={setApiKeySid} placeholder="SK..." autoComplete="off" mono />
            </Field>

            <Field
              label={`${t("settings.telephony.apiKeySecret")} *`}
              hint={s?.twilio.apiKeySecretSet && !apiKeySecret ? t("settings.telephony.secretKept") : undefined}
            >
              <SecretPill
                value={apiKeySecret}
                onChange={setApiKeySecret}
                placeholder={s?.twilio.apiKeySecretSet ? "••••••••" : ""}
              />
            </Field>

            <Field label={`${t("settings.telephony.twimlAppSid")} *`}>
              <Pill value={twimlAppSid} onChange={setTwimlAppSid} placeholder="AP..." autoComplete="off" mono />
            </Field>

            <Field label={t("settings.telephony.pushCredentialSid")} hint={t("settings.telephony.pushCredentialHint")}>
              <Pill
                value={pushCredentialSid}
                onChange={setPushCredentialSid}
                placeholder="CR..."
                autoComplete="off"
                mono
              />
            </Field>

            <Field label={t("settings.telephony.region")} hint={t("settings.telephony.regionHint")}>
              <SelectPill value={region} onChange={setRegion}>
                {REGION_OPTIONS.map((opt) => (
                  <option key={opt || "default"} value={opt}>
                    {opt || t("settings.telephony.regionDefault")}
                  </option>
                ))}
              </SelectPill>
            </Field>

            <Field label={t("settings.telephony.edge")} hint={t("settings.telephony.edgeHint")}>
              <SelectPill value={edge} onChange={setEdge}>
                {EDGE_OPTIONS.map((opt) => (
                  <option key={opt || "default"} value={opt}>
                    {opt || t("settings.telephony.edgeDefault")}
                  </option>
                ))}
              </SelectPill>
            </Field>
          </div>
        </FieldGroup>

        <FieldGroup title={t("settings.telephony.calling")}>
          {/* The one pairing that is a legal problem rather than a preference:
              recording ships ON and the announcement ships OFF, so an install
              where somebody fills in this card and starts dialling stores every
              conversation without a word to the other side. Said on the page
              that switches recording on, because this is one of the two places
              it can be fixed. */}
          {s?.recordsWithoutNotice ? (
            <Note tone="bad">{t("settings.telephony.recordsWithoutNotice")}</Note>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t("settings.telephony.callerId")} *`} hint={t("settings.telephony.callerIdHint")}>
              <Pill value={callerId} onChange={setCallerId} placeholder="+372..." autoComplete="off" mono />
            </Field>
            {/* No `sm:pt-7` any more: the hint makes this two lines tall, so
                aligning it with the pill beside it would push the label off. */}
            <div className="flex items-center">
              <ToggleRow
                label={t("settings.telephony.record")}
                hint={t("settings.telephony.recordHint")}
                checked={record}
                onChange={setRecord}
              />
            </div>
            {/* Here rather than in the bridge group below, even though the bug it
                closes was a bridge bug: this is how long the CUSTOMER's phone
                rings, which is true of both bridges. Under the conference toggle
                it would read as one of that toggle's settings. */}
            <Field
              label={t("settings.telephony.outboundRingSeconds")}
              hint={t("settings.telephony.outboundRingSecondsHint")}
            >
              <Pill
                value={outboundRingSeconds}
                onChange={setOutboundRingSeconds}
                inputMode="numeric"
                placeholder="30"
              />
            </Field>
          </div>
        </FieldGroup>

        <FieldGroup title={t("settings.telephony.bridge")}>
          <div className="space-y-4">
            <ToggleRow
              label={t("settings.telephony.conference")}
              hint={t("settings.telephony.conferenceHint")}
              checked={conference}
              onChange={setConference}
            />
            <HoldMusicField
              value={holdUrl}
              onChange={setHoldUrl}
              saved={s?.holdMusic ?? null}
              stray={s?.holdMusicStray ?? null}
            />
            <RingtoneField value={ringtoneUrl} onChange={setRingtoneUrl} />
            <RingtoneField kind="outgoing" value={ringbackUrl} onChange={setRingbackUrl} />
            <ToggleRow
              label={t("settings.telephony.legDualSplit")}
              hint={t("settings.telephony.legDualSplitHint")}
              checked={legDualSplit}
              onChange={setLegDualSplit}
            />
          </div>
        </FieldGroup>

        <FieldGroup title={t("settings.telephony.webhooks")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("settings.telephony.publicBaseUrl")}
              hint={t("settings.telephony.publicBaseUrlHint")}
            >
              <Pill
                value={publicBaseUrl}
                onChange={setPublicBaseUrl}
                placeholder="https://api.husly.app"
                autoComplete="off"
                mono
              />
            </Field>
            <Field
              label={t("settings.telephony.voiceWebhookUrl")}
              hint={t("settings.telephony.voiceWebhookHint")}
            >
              <CopyPill
                value={s?.voiceWebhookUrl ?? ""}
                copyLabel={t("settings.telephony.copy")}
                copiedLabel={t("settings.telephony.copied")}
              />
            </Field>
          </div>
        </FieldGroup>

        {testResult ? (
          <>
            <Note
              tone={testResult.twilio.ok ? "ok" : "bad"}
              icon={
                testResult.twilio.ok ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                )
              }
              live
            >
              {testResult.twilio.ok
                ? t("settings.telephony.testOk", { detail: testResult.twilio.detail ?? "" })
                : t("settings.telephony.testFail", { error: testResult.twilio.error ?? "" })}
            </Note>
            {/* The line above only ever proved that the account SID and auth token
                authenticate. Everything that has really broken here lives in these
                four rows - see lib/diagnostics.ts for what each one costs. */}
            {checklist.map((line) => (
              <Note
                key={line.id}
                tone={line.tone}
                icon={
                  line.tone === "bad" ? <AlertCircle className="mt-0.5 size-4 shrink-0" /> : undefined
                }
                live
              >
                {t(`settings.telephony.check.${line.id}.label`)}
                {": "}
                {t(`settings.telephony.check.${line.key}`, { detail: line.detail })}
              </Note>
            ))}
          </>
        ) : null}

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.telephony.saving") : t("settings.telephony.save")}
          </Btn>
          <Btn onClick={handleTest} disabled={testState === "testing"}>
            {testState === "testing" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <PlugZap className="size-3.5" />
            )}
            {testState === "testing" ? t("settings.telephony.testing") : t("settings.telephony.test")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.telephony.saved")}</StatusPill> : null}
          {saveState === "error" ? (
            <StatusPill tone="lt">{t("settings.telephony.saveError")}</StatusPill>
          ) : null}
          {/* Its own line rather than a pill: this one has to say what to do,
              and it does not fit in the space a status word gets. */}
          {saveState === "secretRequired" ? (
            <Note tone="warn">{t("settings.telephony.secretRequired")}</Note>
          ) : null}
        </SaveRow>
      </form>

      {/* Outside the credentials form, and deliberately so: these two save on
          their own button and one of them starts spending money. Folding them
          into the form above would mean somebody correcting a typo in an API
          key also silently posted a billing decision. */}
      <div className="mt-5 border-t border-[--crm-line] pt-4">
        <LookupFields status={s} onSaved={setS} />
      </div>
    </IntegrationCard>
  );
}
